/* The taxonomy, in words.
 *
 * `taxonomy.ts` says what an order type is; this says what it is called. Keeping
 * the two apart is what lets the structural table be read backwards by the
 * mapper and forwards by the screens, in either language, from one definition.
 *
 * Split from `useVocabulary.ts` for the same reason `format.ts` is split from
 * `useFormatter.ts`: CSV export and the chart roll-ups in `summaries.ts` need
 * these words and are not components. This file is the logic; the hook is the
 * adapter.
 */

import type { TFunction } from 'i18next';

import type { FunnelLabelKey, SignalLabelKey, SignalValue } from '@/shared/domain/metrics';
import type { SegmentLabelKey } from '@/shared/domain/summaries';
import type enDomain from '@/shared/i18n/locales/en/domain.json';
import type {
  CaseRecord,
  DeskKey,
  EvidenceItem,
  OrderTypeKey,
  QueueKey,
} from '@/shared/domain/case';
import type { ThreadMessage } from '@/shared/domain/conversation';
import type { MailRecord } from '@/shared/domain/intake';
import type { CatalogMatch, Offering } from '@/shared/domain/serviceCatalog';
import { DASH, SEPARATOR } from '@/shared/i18n/format';

/** Every caption a roll-up can carry. The roll-ups in `summaries.ts` and
 *  `metrics.ts` do arithmetic and hand back a key, never a word — so a language
 *  switch redraws a legend without recounting anything. This is where the key
 *  becomes readable. */
export type LabelKey = SegmentLabelKey | FunnelLabelKey | SignalLabelKey;

/** The parts of a case its state and its recommended action are assembled from.
 *
 * Narrower than `CaseRecord` because that is all either needs, and a narrow
 * parameter is what lets a test state the case it is about in four fields
 * rather than twenty. */
export type CaseStatus = Pick<
  CaseRecord,
  'statusKey' | 'statusToken' | 'halted' | 'stopReason'
>;

/** A row that names a customer, or does not. Both mappers emit this pair and
 *  the pair is what makes "no name" answerable: the id says whether there was a
 *  customer to name at all. */
export type Named = Pick<CaseRecord, 'customer' | 'tenant'>;

/** What became of an intake item, in the two fields the word is chosen from. */
export type MailStatus = Pick<MailRecord, 'statusKey' | 'statusToken'>;

/** Who said something, in the three fields the header line is written from. */
export type Speaker = Pick<ThreadMessage, 'who' | 'whoKey' | 'draft'>;

/** What was said, and — where nothing was — what arrived instead. */
export type MessageBody = Pick<ThreadMessage, 'text' | 'source'>;

/** A cited source, in the fields its heading is written from. */
export type EvidenceSource = Pick<EvidenceItem, 'title' | 'kindKey' | 'source'>;

/** Where a candidate may be ordered, in the two fields the phrase is written
 *  from. */
export type OrderableIn = Pick<Offering, 'regions' | 'globalScope'>;

/** What joins `Automation halted` to the reason it was halted for. Punctuation
    rather than wording, for the same reason `SEPARATOR` is. */
const REASON = ' — ';

/* Tokens with colons in them, and the one catalogue that cannot hold them.
 *
 * `case:signOff` is how the API spells a capability and how it must stay spelled
 * on the wire. It cannot be an i18next key: i18next reads the first colon as the
 * namespace separator, so `t('capabilities.case:signOff')` would go looking for
 * a namespace called `capabilities.case`, find none, and render the key back.
 *
 * So the catalogue is keyed by a camel-case leaf and the wire tokens are mapped
 * onto it here. The table is the only place the two spellings meet, which is
 * what makes a capability added to `roles.py` and forgotten here show up as its
 * own token on screen rather than as a blank cell — the same `key ?? token`
 * bargain the rest of this file makes.
 *
 * The value type is read from the English catalogue rather than being `string`,
 * for two reasons. It fails the typecheck on a leaf that no catalogue block
 * defines — a mapping to nowhere renders the key at an operator, which is worse
 * than rendering the wire token. And it is what makes the template literal in
 * `t()` below resolve to a known key at all: `roles.${string}` is not a key,
 * `roles.orderManager | roles.orderManagerAdmin` is.
 */
type DomainLeaf<Block extends keyof typeof enDomain> = keyof (typeof enDomain)[Block] & string;

const CAPABILITY_LEAVES: Readonly<Record<string, DomainLeaf<'capabilities'>>> = {
  'case:work': 'caseWork',
  'case:signOff': 'caseSignOff',
  'clarification:draft': 'clarificationDraft',
  'clarification:dispatch': 'clarificationDispatch',
  'intake:write': 'intakeWrite',
  'intake:rebind': 'intakeRebind',
  'intake:quarantineRelease': 'intakeQuarantineRelease',
  'mri:extract': 'mriExtract',
  'requester:administer': 'requesterAdminister',
  'tenantGroup:administer': 'tenantGroupAdminister',
  'tenant:administer': 'tenantAdminister',
};

/** The roles this cockpit has words for. Deliberately not `desks.*`, though
 *  `Order Manager` appears in both: a desk is where a case sits and a role is
 *  what a person was assigned, and the day one of them is renamed the other
 *  should not follow it. */
const ROLE_LEAVES: Readonly<Record<string, DomainLeaf<'roles'>>> = {
  ordermanager: 'orderManager',
  ordermanageradmin: 'orderManagerAdmin',
};

const LIMIT_LEAVES: Readonly<Record<string, DomainLeaf<'structuralLimits'>>> = {
  ordering: 'ordering',
  automatedApproval: 'automatedApproval',
};

/** The customer states this cockpit has words for, keyed casefolded. */
const STATUS_LEAVES: Readonly<Record<string, DomainLeaf<'customerStatuses'>>> = {
  active: 'active',
  suspended: 'suspended',
};

export interface Vocabulary {
  /** `New provide` / `Neubereitstellung` — the name of a classification. */
  orderType(key: OrderTypeKey): string;
  /** `Work queue` / `Arbeitsvorrat` — the name of a queue. */
  queue(key: QueueKey): string;
  /** The one-line explanation of what lands in a queue, for a subtitle. */
  queueNote(key: QueueKey): string;
  /** The caption a chart segment, funnel stage or indicator carries. */
  label(key: LabelKey): string;
  /** An indicator's reading — `87%`, or a ratio joined by the right word. */
  signal(value: SignalValue): string;
  /** Where the case stands — `MRI complete`, or `Analysing · halted` where
   *  automation stopped on it. */
  caseState(record: CaseStatus): string;
  /** The step the cockpit recommends next, phrased as an instruction to the
   *  person reading it. A recommendation, not a workflow: nothing enforces it,
   *  and states with nothing for a person to do say so rather than inventing
   *  busywork. */
  caseAction(record: CaseStatus): string;
  /** `Order Manager` / `Auftragsmanagement` — the desk holding the case. */
  desk(key: DeskKey): string;
  /** The customer on a case row: their name, `Unknown customer` where the case
   *  is anchored to a tenant this cockpit could not name, and `Unresolved`
   *  where it was never anchored to one at all. Never the id — see the comment
   *  on `toCaseRecord()`. */
  caseCustomer(row: Named): string;
  /** The customer on an intake row. The first two answers are the same; a
   *  message no case has claimed gets a dash rather than a word, because
   *  nothing has yet made a claim about its customer either way. */
  mailCustomer(row: Named): string;
  /** The scope a clarification template was written for: the customer's name,
   *  `Unknown customer` where it is tenant-scoped to one this cockpit could not
   *  name, and `All customers` for the fallback template, which is scoped to no
   *  tenant because it answers for every customer without one of their own. */
  templateCustomer(row: Named): string;
  /** The subject line, or the cockpit's phrase for a message that carried
   *  none. */
  mailSubject(subject: string | null): string;
  /** What became of an intake item — `Case created`, or the API's own token
   *  where this cockpit has no sentence for it. */
  mailStatus(mail: MailStatus): string;
  /** Why the catalog reached the verdict it did, with the lifecycle the gate
   *  ran against appended where there was one. */
  catalogNote(match: CatalogMatch): string;
  /** The name on a message's header line: the speaker's own, or the cockpit's
   *  word for a message that named nobody — and marked as a proposal where the
   *  message has not been sent. */
  speaker(message: Speaker): string;
  /** What a message says, or the cockpit's account of a message that arrived
   *  with nothing to show. */
  messageText(message: MessageBody): string;
  /** The heading over a cited span: what kind of source it is, then where in
   *  that source the span sits. The locator is passed separately because it is
   *  the key the evidence is filed under, and an item that is missing entirely
   *  still has one — which is why `item` may be undefined and the locator alone
   *  is then the answer. */
  evidenceTitle(item: EvidenceSource | undefined, locator: string): string;
  /** Where a service candidate may be ordered: the catalog's own region names,
   *  `Global` where it is scoped everywhere, and a dash where neither was
   *  recorded. */
  regions(offering: OrderableIn): string;
  /** The cited existing service: its label, `Referenced` where one was cited
   *  and its reference was a uuid, and a dash where none was cited at all. */
  subscription(record: Pick<CaseRecord, 'subscription'>): string;
  /** `Order manager` / `Auftragsmanager` — an app role as the directory
   *  assigned it. A role nobody defined shows its own claimed name, so a
   *  mis-assignment in Entra reads as the wrong role rather than as no role. */
  role(token: string): string;
  /** `Sign off cases` — what a capability token permits, phrased as the act
   *  rather than as the permission, because the profile card reads as a list of
   *  things the operator does. */
  capability(token: string): string;
  /** `Placing an order with a supplier` — one of the two things nobody may do.
   *  Phrased as a gerund, because these are read under the heading `Cannot`. */
  structuralLimit(token: string): string;
  /** `Active` / `Suspended` — a tenant's own state. The API's token where this
   *  cockpit has no word for it. */
  customerStatus(token: string): string;
  /** A list of tokens as one line — `Work cases, Sign off cases` — or the
   *  cockpit's word for an empty one. An operator whose app-role assignment is
   *  missing has an empty capability list, and a blank cell there reads as a
   *  screen that failed to draw rather than as the fact that they were granted
   *  nothing. */
  list(words: readonly string[]): string;
}

/** The `domain` namespace, bound. Every key this file names lives there, so the
 *  namespace is stated once in the signature rather than prefixed onto forty
 *  key literals — and `t` stays type-checked against that namespace alone. */
export function createVocabulary(t: TFunction<'domain'>): Vocabulary {
  /** The name, or null where there is none to give. Shared by the two customer
      renderings, which differ only in what they say when there was no customer
      to name in the first place. */
  const named = (row: Named): string | null =>
    row.customer ?? (row.tenant === null ? null : t('customerValues.unknown'));

  return {
    orderType(key) {
      return t(`orderTypes.${key}`);
    },

    queue(key) {
      return t(`queues.${key}.label`);
    },

    queueNote(key) {
      return t(`queues.${key}.note`);
    },

    label(key) {
      return t(key);
    },

    signal(value) {
      if (value.kind === 'text') return value.text;
      return t('ratio', { value: value.value, total: value.total });
    },

    caseState(record) {
      /* A state with no key shows the API's own token. It is the one string
         that identifies what happened, and a case the cockpit cannot name must
         not read as one it has nothing to say about. */
      const label = record.statusKey
        ? t(`caseStates.${record.statusKey}.label`)
        : record.statusToken;
      return record.halted ? `${label}${SEPARATOR}${t('caseValues.halted')}` : label;
    },

    caseAction(record) {
      if (record.halted) {
        /* The reason comes from the API in whatever language the API wrote it,
           so it is appended rather than translated — and only where there is
           one, so no separator dangles off the end of the sentence. */
        const halted = t('caseValues.haltedAction');
        return record.stopReason ? `${halted}${REASON}${record.stopReason}` : halted;
      }
      return record.statusKey
        ? t(`caseStates.${record.statusKey}.action`)
        : t('caseValues.reviewCase');
    },

    desk(key) {
      return t(`desks.${key}`);
    },

    caseCustomer(row) {
      /* A case with no tenant was never anchored to a customer at all, which is
         a state the queue has a word for — it is what the routing tab means by
         an unresolved sender, and a dash there would read as a missing column
         rather than as the thing the row is about. */
      return named(row) ?? t('customerValues.unresolved');
    },

    mailCustomer(row) {
      // A message no case has claimed has not been judged either way, so the
      // column says nothing rather than asserting the sender did not resolve.
      return named(row) ?? DASH;
    },

    templateCustomer(row) {
      /* A template with no tenant is the one every customer without their own
         is written by, so its scope is a statement rather than a missing name —
         and it is the only one of the three renderings that says something the
         reader could not have worked out from a blank cell. */
      return named(row) ?? t('customerValues.allCustomers');
    },

    mailSubject(subject) {
      return subject ?? t('mailValues.noSubject');
    },

    mailStatus(mail) {
      /* A processing status this cockpit has no sentence for shows the API's
         own token, exactly as `caseState` does: it is the one string that
         identifies what happened to the message. */
      return mail.statusKey ? t(`mailStatuses.${mail.statusKey}`) : mail.statusToken;
    },

    catalogNote(match) {
      const note = match.noteKey ? t(`catalogNotes.${match.noteKey}`) : match.noteToken;
      /* The lifecycle the gate ran against, appended where the gate recorded
         one. Untranslated — it is the catalog's own word for the state the
         verdict was reached under, and renaming it would make the note
         disagree with the catalog screen. */
      return match.lifecycle ? `${note}${SEPARATOR}${match.lifecycle}` : note;
    },

    speaker(message) {
      /* A message that named nobody still has to say something on its header
         line — an empty one reads as a rendering fault rather than as the fact
         that the sender did not resolve, which is precisely what a triage
         reader needs to see. */
      const name = message.who ?? (message.whoKey ? t(`threadValues.${message.whoKey}`) : DASH);
      return message.draft ? t('threadValues.draft', { who: name }) : name;
    },

    messageText(message) {
      /* Not a blank bubble. `body_ref` points into a content store the API does
         not read from, so a subject-less item genuinely has nothing to show —
         and saying which kind of thing arrived is the most the cockpit can
         honestly offer. The source is the API's own word, so it is
         interpolated rather than translated. */
      return message.text ?? t('threadValues.noSubjectLine', { source: message.source ?? DASH });
    },

    evidenceTitle(item, locator) {
      /* A fixture that already wrote the whole heading keeps it. Everything the
         mapper produces arrives with `title: null` and is assembled here. */
      if (item?.title != null) return item.title;
      /* The kind, in the cockpit's word where it has one and in the API's own
         slug where it does not — and neither where the provenance row named no
         kind at all, in which case the locator is the heading on its own. The
         cockpit used to write "Source" there; that named nothing the row had
         not already said and read as a kind rather than as the absence of one. */
      const kind = item?.kindKey ? t(`evidenceKinds.${item.kindKey}`) : item?.source;
      return kind === undefined ? locator : `${kind}${SEPARATOR}${locator}`;
    },

    regions(offering) {
      /* Three answers again. Region names are the catalog's own and travel
         untranslated; `Global` is the cockpit saying the candidate may be
         ordered anywhere, which is a statement rather than a missing value; and
         a candidate with neither has nothing recorded, which is the one case a
         dash is honest for. */
      return offering.regions ?? (offering.globalScope ? t('offeringValues.global') : DASH);
    },

    subscription(record) {
      /* Three answers, because the field has three states and they are three
         different facts. Absent: no service was cited. Null: one was cited and
         its reference was a uuid, which this cockpit does not put on screen —
         so the row says a service is referenced without pretending to name it.
         Otherwise the label itself. */
      if (record.subscription === undefined) return DASH;
      return record.subscription ?? t('caseValues.referenced');
    },

    role(token) {
      const leaf = ROLE_LEAVES[token];
      return leaf === undefined ? token : t(`roles.${leaf}`);
    },

    capability(token) {
      const leaf = CAPABILITY_LEAVES[token];
      return leaf === undefined ? token : t(`capabilities.${leaf}`);
    },

    structuralLimit(token) {
      const leaf = LIMIT_LEAVES[token];
      return leaf === undefined ? token : t(`structuralLimits.${leaf}`);
    },

    customerStatus(token) {
      /* Matched casefolded because this one arrives from two places — the
         tenancy namespace writes `active`, the TMF customer screen has been
         showing whatever the catalog wrote — and a capital A is not a different
         state. */
      const leaf = STATUS_LEAVES[token.toLowerCase()];
      return leaf === undefined ? token : t(`customerStatuses.${leaf}`);
    },

    list(words) {
      /* A comma and a space, not a translated conjunction. These are lists of
         nouns in a table cell, where `Intl.ListFormat`'s "and" would read as a
         sentence and wrap the column. */
      return words.length === 0 ? t('none') : words.join(', ');
    },
  };
}
